import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from '@tanstack/react-query';
import { apiFetch, apiJson, ApiError } from '@/lib/apiClient';
import type { CallbackNumber } from './types';
import type {
  OwnerCallbackNumbersResponseZ,
} from '../../../../api/src/modules/listen/routes';
import type { z } from 'zod';

/**
 * Call-back-number management (slice 86, seam S3). The caller's own numbers
 * (`/api/me/callback-numbers`, ≤3, add/change capped to once per 24 h) and —
 * for an owner — every number in the tenant (`/api/callback-numbers`), with a
 * Remove for managers' numbers.
 *
 * 🔴 EVERY MUTATION HERE IS AN ORDINARY WRITE (402 while suspended). That is the
 * opposite of the Listen call itself, which is allowed suspended. The Settings
 * panel hides these controls for a non-writable session (absence over disabled);
 * the server enforces the 402 regardless.
 */

/** One owner-view row: a number plus whose it is (so an owner can remove a
 *  manager's). Inferred from the api's `OwnerCallbackNumbersResponseZ`. */
export type OwnerCallbackNumber =
  z.infer<typeof OwnerCallbackNumbersResponseZ>['numbers'][number];

/** The failures the UI turns into a sentence. */
export type CallbackFailure =
  | 'invalid_number'
  | 'callback_change_capped'
  | 'callback_limit'
  | 'callback_duplicate'
  | 'unknown';

export function classifyCallbackError(error: unknown): CallbackFailure {
  if (error instanceof ApiError && error.code) {
    const code = error.code;
    if (
      code === 'invalid_number' ||
      code === 'callback_change_capped' ||
      code === 'callback_limit' ||
      code === 'callback_duplicate'
    ) {
      return code;
    }
  }
  return 'unknown';
}

export const callbackNumbersQueryKey = ['callback-numbers', 'mine'] as const;
export const ownerCallbackNumbersQueryKey = ['callback-numbers', 'owner'] as const;

/** The caller's own saved numbers. */
export function useMyCallbackNumbers(): UseQueryResult<CallbackNumber[]> {
  return useQuery({
    queryKey: callbackNumbersQueryKey,
    queryFn: async ({ signal }) => {
      const body = await apiJson<{ numbers: CallbackNumber[] }>(
        '/api/me/callback-numbers',
        { signal },
      );
      return Array.isArray(body?.numbers) ? body.numbers : [];
    },
  });
}

/** The owner view — every number in the tenant. Only call when the user is an
 *  owner; the server 403s otherwise. */
export function useOwnerCallbackNumbers(
  enabled: boolean,
): UseQueryResult<OwnerCallbackNumber[]> {
  return useQuery({
    queryKey: ownerCallbackNumbersQueryKey,
    enabled,
    queryFn: async ({ signal }) => {
      const body = await apiJson<{ numbers: OwnerCallbackNumber[] }>(
        '/api/callback-numbers',
        { signal },
      );
      return Array.isArray(body?.numbers) ? body.numbers : [];
    },
  });
}

function invalidateAll(qc: ReturnType<typeof useQueryClient>): void {
  void qc.invalidateQueries({ queryKey: callbackNumbersQueryKey });
  void qc.invalidateQueries({ queryKey: ownerCallbackNumbersQueryKey });
}

export function useAddCallbackNumber() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { phone: string; label?: string }) =>
      apiJson<CallbackNumber>('/api/me/callback-numbers', {
        method: 'POST',
        body: JSON.stringify(
          input.label && input.label.trim() !== ''
            ? { phone: input.phone, label: input.label.trim() }
            : { phone: input.phone },
        ),
      }),
    onSuccess: () => invalidateAll(qc),
  });
}

export function useEditCallbackNumber() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string; phone?: string; label?: string | null }) => {
      const body: Record<string, unknown> = {};
      if (input.phone !== undefined) body.phone = input.phone;
      if (input.label !== undefined) body.label = input.label;
      return apiJson<CallbackNumber>(
        `/api/me/callback-numbers/${encodeURIComponent(input.id)}`,
        { method: 'PATCH', body: JSON.stringify(body) },
      );
    },
    onSuccess: () => invalidateAll(qc),
  });
}

export function useDeleteCallbackNumber() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await apiFetch(`/api/me/callback-numbers/${encodeURIComponent(id)}`, {
        method: 'DELETE',
      });
      if (!res.ok) throw new ApiError(res.status, null);
    },
    onSuccess: () => invalidateAll(qc),
  });
}

/** Owner removes ANY number in the tenant (a manager's). */
export function useOwnerDeleteCallbackNumber() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await apiFetch(`/api/callback-numbers/${encodeURIComponent(id)}`, {
        method: 'DELETE',
      });
      if (!res.ok) throw new ApiError(res.status, null);
    },
    onSuccess: () => invalidateAll(qc),
  });
}
